import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton } from '@/components/documents';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  Modal,
  OkNote,
  Pager,
  ScreenWithForm,
  SearchBar,
  Select,
  StatusPill,
  Table,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatDate, formatMoney } from '@/format';
import {
  useFinanceMutation,
  useJournal,
  useJournalEntry,
  usePostableAccounts,
  type JournalRow,
} from '@/hooks/finance';
import { useAuth } from '@/stores/auth';

const PAGE = 50;

const KINDS = [
  { value: '', label: 'All' },
  { value: 'SYSTEM', label: 'Written by a document' },
  { value: 'MANUAL', label: 'Typed by hand' },
  { value: 'OPENING', label: 'Opening balances' },
  { value: 'CLOSING', label: 'Year end' },
  { value: 'REVERSAL', label: 'Reversal' },
];

interface LineDraft {
  accountCode: string;
  debit: string;
  credit: string;
  narration: string;
}

const blankLine = (): LineDraft => ({
  accountCode: '',
  debit: '',
  credit: '',
  narration: '',
});

/**
 * Financials › Journal Vouchers.
 *
 * The one screen in the system that says debit and credit, because the one person
 * who opens it speaks them. Depreciation, an accrual, a correction nothing else
 * covers.
 *
 * There is no draft. A voucher that does not balance cannot be saved at all, so
 * there is nothing for a half-finished one to be: the screen holds it until the two
 * sides agree, and then it posts. A posted one is corrected by reversing it, like
 * everything else here.
 */
export function JournalVouchers() {
  const can = useAuth((s) => s.can);

  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [entryDate, setEntryDate] = useState(todayIso());
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([blankLine(), blankLine()]);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const journal = useJournal({ kind, search, take: PAGE, skip });
  const detail = useJournalEntry(opened);
  const accounts = usePostableAccounts();
  const mutate = useFinanceMutation<unknown>();

  const rows = journal.data?.items ?? [];
  const total = journal.data?.total ?? 0;
  const current = rows.find((r) => r.id === selected) ?? null;

  const accountOptions = (accounts.data?.items ?? []).map((a) => ({
    value: a.code,
    label: `${a.code} — ${a.nameEn}`,
  }));

  const totals = useMemo(() => {
    const sum = (key: 'debit' | 'credit') =>
      lines.reduce((acc, l) => {
        const n = Number(l[key]);
        return acc + (Number.isFinite(n) ? n : 0);
      }, 0);
    const debit = sum('debit');
    const credit = sum('credit');
    return {
      debit: debit.toFixed(2),
      credit: credit.toFixed(2),
      balanced: Math.abs(debit - credit) < 0.005 && debit > 0,
      difference: (debit - credit).toFixed(2),
    };
  }, [lines]);

  function reset() {
    setEntryDate(todayIso());
    setNarration('');
    setLines([blankLine(), blankLine()]);
    setError('');
    setNote('');
  }

  const setLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((list) => list.map((l, j) => (i === j ? { ...l, ...patch } : l)));

  async function save() {
    setError('');
    setNote('');
    if (!narration.trim()) return setError('A voucher needs a narration');
    if (!totals.balanced) {
      return setError(
        `This voucher does not balance: debits ${totals.debit}, credits ${totals.credit}`,
      );
    }

    const body = {
      entryDate,
      narration: narration.trim(),
      lines: lines
        .filter((l) => l.accountCode && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          accountCode: l.accountCode,
          ...(Number(l.debit) > 0 ? { debit: l.debit } : {}),
          ...(Number(l.credit) > 0 ? { credit: l.credit } : {}),
          ...(l.narration.trim() ? { narration: l.narration.trim() } : {}),
        })),
    };

    try {
      await mutate.mutateAsync(() => api.post('/api/v1/fin/journal', body));
      setNote('Voucher posted.');
      setShowForm(false);
      reset();
    } catch (e) {
      setError(errorMessage(e, 'That voucher could not be posted'));
    }
  }

  const columns: Array<GridColumn<JournalRow>> = [
    { key: 'entryDate', header: 'Date', width: 104, render: (r) => formatDate(r.entryDate) },
    { key: 'entryNo', header: 'Entry no', width: 140, render: (r) => r.entryNo },
    {
      key: 'kind',
      header: 'Written by',
      width: 150,
      render: (r) => KINDS.find((k) => k.value === r.kind)?.label ?? r.kind,
    },
    {
      key: 'sourceNo',
      header: 'Document',
      width: 140,
      render: (r) => r.sourceNo ?? <span className="muted">—</span>,
    },
    { key: 'narration', header: 'Narration', width: 280, render: (r) => r.narration },
    {
      key: 'entryAmount',
      header: 'Amount',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.entryAmount),
    },
    { key: 'periodLabel', header: 'Month', width: 130, render: (r) => r.periodLabel },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) =>
        r.isReversed ? <StatusPill status="REVERSED" /> : <StatusPill status={r.status} />,
    },
  ];

  const form = (
    <FormPanel
      title="Type a voucher"
      icon={<AppIcon kind="calculate" size={18} />}
      intro="It will not save until the two sides agree, so there is no draft to come back to."
    >
      <FormGroup title="The voucher" columns={2}>
        <Field label="Date" type="date" value={entryDate} onChange={setEntryDate} required />
        <Field
          label="Narration"
          value={narration}
          onChange={setNarration}
          required
          span={2}
          placeholder="Depreciation for September"
        />
      </FormGroup>

      <FormGroup title="Lines">
        <Table
          count={lines.length}
          empty="No lines."
          head={
            <tr>
              <th>Account</th>
              <th className="num">Debit</th>
              <th className="num">Credit</th>
              <th>Note</th>
              <th />
            </tr>
          }
        >
          {lines.map((line, i) => (
            <tr key={i}>
              <td>
                <Select
                  label=""
                  value={line.accountCode}
                  onChange={(v) => setLine(i, { accountCode: v })}
                  options={accountOptions}
                />
              </td>
              <td className="num">
                <Field
                  label=""
                  type="number"
                  value={line.debit}
                  // A line is one side or the other, so typing in one clears the other
                  // rather than letting a line be both.
                  onChange={(v) => setLine(i, { debit: v, credit: v ? '' : line.credit })}
                />
              </td>
              <td className="num">
                <Field
                  label=""
                  type="number"
                  value={line.credit}
                  onChange={(v) => setLine(i, { credit: v, debit: v ? '' : line.debit })}
                />
              </td>
              <td>
                <Field
                  label=""
                  value={line.narration}
                  onChange={(v) => setLine(i, { narration: v })}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  disabled={lines.length <= 2}
                  onClick={() => setLines((list) => list.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </Table>
        <div className="toolbar">
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setLines((list) => [...list, blankLine()])}
          >
            Add a line
          </button>
          <span className={totals.balanced ? 'muted' : 'field__problem'}>
            Debits {formatMoney(totals.debit)} · credits {formatMoney(totals.credit)}
            {totals.balanced ? ' · balanced' : ` · out by ${formatMoney(totals.difference)}`}
          </span>
        </div>
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending || !totals.balanced}
          onClick={() => void save()}
        >
          Post voucher
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('fin.journal.create')}
      addLabel="Type a voucher"
      showForm={showForm}
      onToggle={() => {
        if (showForm) return setShowForm(false);
        reset();
        setShowForm(true);
      }}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <SearchBar
          value={search}
          onChange={(v) => {
            setSearch(v);
            setSkip(0);
          }}
          placeholder="Search entry no, document or narration"
        />
        <Select
          label="Written by"
          value={kind}
          onChange={(v) => {
            setKind(v);
            setSkip(0);
          }}
          options={KINDS}
          placeholder="All"
        />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <ActionBar
        actions={[
          {
            label: 'Open',
            icon: 'details',
            enabled: Boolean(current),
            reason: 'Pick an entry first',
            onSelect: () => current && setOpened(current.id),
          },
        ]}
      >
        {current?.kind === 'MANUAL' && !current.isReversed && can('fin.journal.reverse') && (
          <ReverseButton
            label={`Reverse ${current.entryNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(`/api/v1/fin/journal/${current.id}/reverse`, { reason }),
                )
                .then(() => setNote('Voucher reversed.'))
                .catch((e: unknown) =>
                  // A document's own entry is refused here, and the message says to
                  // reverse the document instead.
                  setError(errorMessage(e, 'That entry could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="fin-journal"
        label="Journal"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => setOpened(row.id)}
        empty="Nothing in the journal yet."
        loading={journal.isLoading}
      />

      <Pager skip={skip} take={PAGE} total={total} onChange={setSkip} />

      <StatusBar>
        <span>
          {total} entr{total === 1 ? 'y' : 'ies'}
        </span>
      </StatusBar>

      {opened && detail.data && (
        <Modal title={`Entry ${detail.data.entryNo}`} onClose={() => setOpened(null)}>
          <p className="muted">
            {formatDate(detail.data.entryDate)} · {detail.data.periodLabel} ·{' '}
            {detail.data.narration}
          </p>
          {detail.data.sourceNo && (
            <p className="muted">Written by {detail.data.sourceType} {detail.data.sourceNo}</p>
          )}
          <Table
            count={detail.data.lines.length}
            empty="No lines."
            head={
              <tr>
                <th>Account</th>
                <th>Farm</th>
                <th>Note</th>
                <th className="num">Debit</th>
                <th className="num">Credit</th>
              </tr>
            }
          >
            {detail.data.lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.accountCode} — {l.accountName}
                </td>
                <td>{l.farmName ?? <span className="muted">head office</span>}</td>
                <td>{l.narration ?? ''}</td>
                <td className="num">{l.debit === '0.00' ? '' : formatMoney(l.debit)}</td>
                <td className="num">{l.credit === '0.00' ? '' : formatMoney(l.credit)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3}>
                <strong>Total</strong>
              </td>
              <td className="num">
                <strong>{formatMoney(detail.data.totalDebit)}</strong>
              </td>
              <td className="num">
                <strong>{formatMoney(detail.data.totalCredit)}</strong>
              </td>
            </tr>
          </Table>
        </Modal>
      )}
    </ScreenWithForm>
  );
}
